import { GoogleReCaptcha } from 'react-google-recaptcha-v3';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { EntryPath } from '@/Config/app/router/entry.path';
import { ReCaptchaCheckbox } from '@/Domain/components/Auth/ReCaptchaCheckbox';
import { GoogleButton } from '@/Domain/components/Auth/SocialButton/GoogleButton';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { Input } from '@/Domain/components/Shared/Input/Input';
import { Spinner } from '@/Domain/components/Shared/Spinner';
import { SocialPlatformEnum } from '@/Domain/enum/SocialPlatform.enum';
import { useLogin } from '@/Domain/hooks/Auth/useLogin.hook';
import { useSocialLogin } from '@/Domain/hooks/Auth/useSocialLogin.hook';

export const LoginPage = () => {
  const { t } = useTranslation('login');

  const {
    useV2,
    control,
    register,
    handleSubmit,
    errors,
    isPending,
    onSubmit,
    onVerify,
    tooManyRequests,
  } = useLogin();

  const { handleSocialLogin } = useSocialLogin();

  return (
    <>
      <h1 className="font-semibold mb-8 text-xl text-center text-black">{t('title')}</h1>
      <div className="d-grid gap-4">
        <div className="d-grid gap-4 mb-2">
          <GoogleButton
            onClick={(event) =>
              handleSocialLogin(event, SocialPlatformEnum.Google, '/login/google-oauth2/')
            }
          />
        </div>
        <div className="relative flex py-1 items-center">
          <div className="flex-grow border-t border-gray-400"></div>
          <span className="flex-shrink mx-4 text-gray-400 text-xs">{t('or')}</span>
          <div className="flex-grow border-t border-gray-400"></div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className=" flex flex-col gap-4">
          <GoogleReCaptcha onVerify={onVerify} />
          <div>
            <label htmlFor="email" className="text-sm text-gray-950">
              {t('form.email')}
            </label>
            <Input
              id="email"
              {...register('username')}
              placeholder={t('form.email_placeholder')}
              error={!!errors.username}
            />
            {errors.username && (
              <span className="text-sm text-red-700">{errors.username.message}</span>
            )}
          </div>

          <div>
            <label htmlFor="password" className="text-sm text-gray-950">
              {t('form.password')}
            </label>
            <Input
              id="password"
              type="password"
              placeholder={t('form.password_placeholder')}
              {...register('password')}
              error={!!errors.password}
            />
            {errors.password && (
              <span className="text-sm text-red-700">{errors.password.message}</span>
            )}
          </div>

          {tooManyRequests && (
            <p className="text-sm text-red-700">{t('ERRORS:TOO_MANY_REQUESTS')}</p>
          )}

          <div className="text-right">
            <Link to={EntryPath.forgetPassword} className="text-xs !text-gray-950 font-medium">
              {t('link.forgot_password')}
            </Link>
          </div>

          <Button variant="dark" type="submit" disabled={isPending} className="w-full">
            {isPending ? <Spinner /> : t('button.login')}
          </Button>

          {useV2 && (
            <Controller
              name="recaptcha_response_v2"
              control={control}
              rules={{
                required: true,
              }}
              render={({ field: { onChange }, fieldState: { invalid } }) => (
                <ReCaptchaCheckbox onChange={onChange} isInvalid={invalid} />
              )}
            />
          )}
        </form>

        <div className="text-center mt-4">
          <p className="text-sm">
            <Link to={EntryPath.register} className="text-gray-950 font-medium">
              {t('link.register')}
            </Link>
          </p>
        </div>
      </div>
    </>
  );
};

export default LoginPage;
